/**
 * Organization Entitlements Tab (Superadmin Only)
 *
 * Manage plan tier, limits, and add-ons.
 */

import { useState } from 'react';
import { doc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '@/lib/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { logAdminAction, ACTION_DESCRIPTIONS, createDiff } from '@/lib/admin/auditLog';
import { AdminCard } from '@/components/admin/AdminShell';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2, Save } from 'lucide-react';
import { OrgEntitlements, OrgPlanTier } from '@/integrations/firebase/types';

interface OrgEntitlementsTabProps {
  orgId: string;
  entitlements: OrgEntitlements | null;
  onUpdate: (entitlements: OrgEntitlements) => void;
}

const PLAN_TIERS: { value: OrgPlanTier; label: string }[] = [
  { value: 'core', label: 'Core' },
  { value: 'professional', label: 'Professional' },
  { value: 'enterprise', label: 'Enterprise' },
];

export function OrgEntitlementsTab({ orgId, entitlements, onUpdate }: OrgEntitlementsTabProps) {
  const { user } = useAuth();
  const { adminUser } = useAdminAuth();
  const { toast } = useToast();

  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    planTier: entitlements?.planTier || 'core',
    seatsLimit: entitlements?.seatsLimit || 3,
    checksLimitMonthly: entitlements?.checksLimitMonthly || 10,
    freeCheckCredits: entitlements?.freeCheckCredits || 1,
    prioritySupport: entitlements?.addons?.prioritySupport || false,
    extraExports: entitlements?.addons?.extraExports || false,
  });

  const handleSave = async () => {
    if (!user?.uid || !adminUser) return;

    try {
      setSaving(true);

      const updatedEntitlements: OrgEntitlements = {
        orgId,
        planTier: formData.planTier as OrgPlanTier,
        seatsLimit: formData.seatsLimit,
        checksLimitMonthly: formData.checksLimitMonthly,
        freeCheckCredits: formData.freeCheckCredits,
        addons: {
          prioritySupport: formData.prioritySupport,
          extraExports: formData.extraExports,
        },
        updatedAt: serverTimestamp() as any,
        updatedBy: user.uid,
      };

      await updateDoc(doc(db, 'org_entitlements', orgId), updatedEntitlements);

      await logAdminAction({
        actorUserId: user.uid,
        actorEmail: adminUser.email,
        actorRole: adminUser.adminRole,
        actionType: 'SET_PLAN',
        targetOrgId: orgId,
        description: ACTION_DESCRIPTIONS.SET_PLAN(orgId),
        diff: entitlements ? createDiff(entitlements, updatedEntitlements) : undefined,
      });

      onUpdate(updatedEntitlements);

      toast({
        title: 'Entitlements Updated',
        description: 'Plan and limits have been saved',
      });
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-medium text-white mb-1">Plan & Entitlements</h2>
        <p className="text-sm text-gray-400">
          Configure billing tier, limits, and add-on features
        </p>
      </div>

      {/* Plan Tier */}
      <AdminCard>
        <h3 className="text-sm font-medium text-white mb-4">Plan Tier</h3>
        <div className="grid grid-cols-3 gap-3">
          {PLAN_TIERS.map((tier) => (
            <button
              key={tier.value}
              onClick={() => setFormData((prev) => ({ ...prev, planTier: tier.value }))}
              className={`p-4 rounded-lg border text-center transition-all ${
                formData.planTier === tier.value
                  ? 'border-[hsl(220,70%,50%)] bg-[hsl(220,70%,50%,0.1)]'
                  : 'border-[hsl(220,15%,25%)] bg-[hsl(220,15%,12%)] hover:border-[hsl(220,15%,35%)]'
              }`}
            >
              <p className="font-medium text-white">{tier.label}</p>
            </button>
          ))}
        </div>
      </AdminCard>

      {/* Limits */}
      <AdminCard>
        <h3 className="text-sm font-medium text-white mb-4">Limits</h3>
        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label className="text-gray-300">Seats Limit</Label>
            <Input
              type="number"
              min={1}
              value={formData.seatsLimit}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  seatsLimit: parseInt(e.target.value) || 1,
                }))
              }
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Checks/Month</Label>
            <Input
              type="number"
              min={0}
              value={formData.checksLimitMonthly}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  checksLimitMonthly: parseInt(e.target.value) || 0,
                }))
              }
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white"
            />
            <p className="text-xs text-gray-500">-1 for unlimited</p>
          </div>
          <div className="space-y-2">
            <Label className="text-gray-300">Free Check Credits</Label>
            <Input
              type="number"
              min={0}
              value={formData.freeCheckCredits}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  freeCheckCredits: parseInt(e.target.value) || 0,
                }))
              }
              className="bg-[hsl(220,20%,15%)] border-[hsl(220,15%,25%)] text-white"
            />
          </div>
        </div>
      </AdminCard>

      {/* Add-ons */}
      <AdminCard>
        <h3 className="text-sm font-medium text-white mb-4">Add-ons</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Priority Support</p>
              <p className="text-xs text-gray-500">24hr response time</p>
            </div>
            <Switch
              checked={formData.prioritySupport}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, prioritySupport: checked }))
              }
            />
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-white">Extra Exports</p>
              <p className="text-xs text-gray-500">PDF/CSV export capabilities</p>
            </div>
            <Switch
              checked={formData.extraExports}
              onCheckedChange={(checked) =>
                setFormData((prev) => ({ ...prev, extraExports: checked }))
              }
            />
          </div>
        </div>
      </AdminCard>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button
          onClick={handleSave}
          disabled={saving}
          className="bg-[hsl(220,70%,50%)] hover:bg-[hsl(220,70%,45%)]"
        >
          {saving ? (
            <>
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="h-4 w-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
